import { useCallback, useState } from 'react';
import useClientSelect from '../Clients/hooks/useClientSelect';
import useCreatorExploreUrlState, { mapToApiParams } from './hooks/useCreatorExploreUrlState';
import { useCreatorExplorePlatformSelection } from './hooks/useCreatorExplorePlatformSelection';
import { getActiveFilterCount, mapFiltersToUrlState, getFiltersFromUrlState, Filters } from './utils/filters';
import { useCreatorExplore } from './hooks/useCreatorExplore';
import { CreatorExploreTable } from './components/CreatorExploreTable/CreatorExploreTable';
import { FilterSidebar } from './components/FilterSidebar/FilterSidebar';
import styles from './CreatorExplore.module.css';
import {
    DEFAULT_PRESET_ID,
    PAGE_SIZE,
    PRESET_COLUMN_IDS,
    PresetId,
    VISIBILITY_PRESETS,
    getCreatorDetailsAccount,
    isHideableColumn,
} from './components/CreatorExploreTable/helpers';
import {
    Badge,
    Button,
    ButtonGroup,
    DropdownMenu,
    SearchInput,
    ColumnPresetsMenu,
    lucideIcons,
    Skeleton,
    Select,
    InputGroup,
    Tooltip,
} from '@round/creatorbase-ui';
import cn from 'classnames';
import {
    mapApiParamsToSort,
    mapSortToApiParams,
} from '../Project/ProjectDetails/components/AddCreatorsToCampaignModal/helpers';
import { SortingState } from '@tanstack/react-table';
import { influencer } from '@round/api';
import AddCreatorModal from './components/AddCreatorModal/AddCreatorModal';
import CreatorDetailsModal from './components/CreatorDetailsModal/CreatorDetailsModal';
import BulkAddCreatorsModal from './components/BulkAddCreatorsModal/BulkAddCreatorsModal';

import { ModalConfig } from './components/CreatorDetailsModal/helpers';

const { ChevronDown, Filter, Info, Plus, Sparkles, Users } = lucideIcons;

const BLURB_SEARCH_TITLE = 'AI Search';
const BLURB_SEARCH_DESCRIPTION =
    'Describe the creator in plain language and results are ranked by how well their profile matches.';

const BLURB_SEARCH_ENGINE_OPTIONS = [
    { value: 'mongo', label: 'Quick Search' },
    { value: 'jev', label: 'Extended Search' },
];

type CreatorModalConfig = ModalConfig & {
    platform: 'tiktok' | 'instagram';
};

export const CreatorExplore = () => {
    const { selectedClientId } = useClientSelect();

    const [showFilters, setShowFilters] = useState(false);
    const [selectedUserModalConfig, setSelectedUserModalConfig] = useState<CreatorModalConfig | null>(null);
    const [expandedCreatorId, setExpandedCreatorId] = useState<string | null>(null);

    const { urlState, setUrlState } = useCreatorExploreUrlState();

    const activePreset = urlState.visibilityPreset ?? DEFAULT_PRESET_ID;
    const activeFilterCount = getActiveFilterCount(getFiltersFromUrlState(urlState));

    const {
        data,
        getCreatorImages,
        getAreCreatorImagesLoading,
        isLoading,
        hasError,
        count,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        invalidate: invalidateData,
    } = useCreatorExplore(
        {
            page_size: PAGE_SIZE,
            client_id: selectedClientId,
            ...mapToApiParams(urlState),
        },
        { enabled: typeof selectedClientId === 'number' }
    );

    const { getRowSelectedPlatform, onRowPlatformSelect, resetSelectedPlatforms } =
        useCreatorExplorePlatformSelection();

    const handleFiltersChange = useCallback(
        (filters: Filters) => {
            setUrlState(mapFiltersToUrlState(filters));
            resetSelectedPlatforms();
        },
        [setUrlState, resetSelectedPlatforms]
    );

    const handleSortingChange = useCallback(
        (sorting: SortingState) => {
            setUrlState(mapSortToApiParams(sorting));
            resetSelectedPlatforms();
        },
        [setUrlState, resetSelectedPlatforms]
    );

    const [isAddCreatorModalOpen, setIsAddCreatorModalOpen] = useState(false);
    const [isBulkAddCreatorsModalOpen, setIsBulkAddCreatorsModalOpen] = useState(false);

    const handleOpenCreatorDetails = useCallback((creator: influencer.CreatorSearchResult) => {
        const details = getCreatorDetailsAccount(creator);
        if (!details) {
            return;
        }

        setSelectedUserModalConfig({
            clientInfluencerAccountId: details.account.creatorbase_client_influencer_account_id,
            platform: details.platform,
            platformUserId: details.account.creatorbase_user_id,
        });
    }, []);

    const handleCloseUserModal = useCallback(
        (hasUpdated: boolean) => {
            setSelectedUserModalConfig(null);
            if (hasUpdated) {
                invalidateData();
            }
        },
        [invalidateData]
    );

    return (
        <>
            <div className={cn(styles.container, 'twp')}>
                <header className={cn(styles.section, styles.header)}>
                    <h1 className={styles.title}>Creator Explore</h1>

                    <ButtonGroup>
                        <Button size="xs" onClick={() => setIsAddCreatorModalOpen(true)}>
                            <Plus /> Creator
                        </Button>
                        <ButtonGroup.Separator />
                        <DropdownMenu>
                            <DropdownMenu.Trigger asChild>
                                <Button size="xs" aria-label="More ways to add creators">
                                    <ChevronDown />
                                </Button>
                            </DropdownMenu.Trigger>
                            {/* The menu is sized to its trigger by default, which is a chevron here. */}
                            <DropdownMenu.Content align="end" className={styles.addCreatorMenu}>
                                <DropdownMenu.Item onSelect={() => setIsBulkAddCreatorsModalOpen(true)}>
                                    <Users aria-hidden="true" />
                                    Bulk add creators
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu>
                    </ButtonGroup>
                </header>

                <div className={cn(styles.section, styles.toolbar)}>
                    <Button
                        variant="outline"
                        size="xs"
                        className={styles.filterButton}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <Filter className={styles.filterButtonIcon} />
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                        {!showFilters && activeFilterCount > 0 && <Badge size="sm">{activeFilterCount}</Badge>}
                    </Button>

                    <SearchInput
                        placeholder="Search creators..."
                        value={urlState.search}
                        onChange={(e) => setUrlState({ search: e.target.value })}
                        className={styles.search}
                        debounce={700}
                    />

                    <SearchInput
                        placeholder="Describe the creator..."
                        value={urlState.blurbSearch}
                        onChange={(e) => setUrlState({ blurbSearch: e.target.value })}
                        className={styles.blurbSearch}
                        debounce={700}
                        icon={<Sparkles />}
                    >
                        <InputGroup.Addon align="inline-end">
                            <Tooltip>
                                <Tooltip.Trigger asChild>
                                    <Info
                                        className={styles.blurbSearchInfo}
                                        aria-label={`${BLURB_SEARCH_TITLE}: ${BLURB_SEARCH_DESCRIPTION}`}
                                        tabIndex={0}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Content className="max-w-72">
                                    <div className="font-semibold">{BLURB_SEARCH_TITLE}</div>
                                    <div>{BLURB_SEARCH_DESCRIPTION}</div>
                                </Tooltip.Content>
                            </Tooltip>
                        </InputGroup.Addon>
                    </SearchInput>
                    <Select
                        options={BLURB_SEARCH_ENGINE_OPTIONS}
                        value={urlState.blurbSearchEngine === 'jev' ? 'jev' : 'mongo'}
                        onChange={(value) => setUrlState({ blurbSearchEngine: value === 'jev' ? 'jev' : undefined })}
                        className={styles.blurbSearchEngine}
                    />

                    <span className={styles.countLabel}>
                        {isLoading ? (
                            <Skeleton className={styles.countSkeleton} />
                        ) : (
                            `${count} creator${count !== 1 ? 's' : ''}`
                        )}
                    </span>

                    <ColumnPresetsMenu
                        options={VISIBILITY_PRESETS.map((p) => ({
                            value: p.id,
                            label: p.name,
                        }))}
                        value={activePreset}
                        onChange={(visibilityPreset) => {
                            const nextColumns =
                                PRESET_COLUMN_IDS[visibilityPreset as PresetId] ?? PRESET_COLUMN_IDS[DEFAULT_PRESET_ID];
                            const sortedColumn = urlState.order_by_asc || urlState.order_by_desc;
                            const shouldClearSort =
                                sortedColumn && isHideableColumn(sortedColumn) && !nextColumns.includes(sortedColumn);
                            setUrlState({
                                visibilityPreset,
                                ...(shouldClearSort ? { order_by_asc: undefined, order_by_desc: undefined } : {}),
                            });
                        }}
                        className={styles.columnPresetsMenu}
                    />
                </div>

                <div className={styles.contentRow}>
                    {showFilters && (
                        <aside className={styles.sidebar}>
                            <FilterSidebar
                                filters={getFiltersFromUrlState(urlState)}
                                onFiltersChange={handleFiltersChange}
                            />
                        </aside>
                    )}
                    <section className={cn(styles.section, styles.tableSection)}>
                        <CreatorExploreTable
                            data={data}
                            meta={{
                                isLoading,
                                hasError,
                                getCreatorImages,
                                getAreCreatorImagesLoading,
                                getRowSelectedPlatform,
                                onRowPlatformSelect,
                                onOpenCreatorDetails: handleOpenCreatorDetails,
                            }}
                            sorting={mapApiParamsToSort({
                                order_by_asc: urlState.order_by_asc,
                                order_by_desc: urlState.order_by_desc,
                            })}
                            onSortingChange={handleSortingChange}
                            fetchNextPage={() => {
                                if (hasNextPage && !isFetchingNextPage) {
                                    void fetchNextPage();
                                }
                            }}
                            activePreset={activePreset}
                            showMatchScore={!!urlState.blurbSearch}
                            expandedCreatorId={expandedCreatorId}
                            onRowClick={(creator) =>
                                setExpandedCreatorId((previous) => (previous === creator._id ? null : creator._id))
                            }
                        />
                    </section>
                </div>
            </div>

            <CreatorDetailsModal
                key={selectedUserModalConfig?.clientInfluencerAccountId}
                isOpen={!!selectedUserModalConfig}
                onClose={handleCloseUserModal}
                clientInfluencerAccountId={selectedUserModalConfig?.clientInfluencerAccountId}
            />

            <AddCreatorModal
                isOpen={isAddCreatorModalOpen}
                onClose={() => setIsAddCreatorModalOpen(false)}
                onCreated={invalidateData}
            />

            <BulkAddCreatorsModal
                isOpen={isBulkAddCreatorsModalOpen}
                onClose={() => setIsBulkAddCreatorsModalOpen(false)}
            />
        </>
    );
};
